import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import Collapse from "@mui/material/Collapse";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";

import ReportesApi from "api/reportesApi";

/**
 * Componente de filtros para reportes
 * Proporciona campos de filtrado y botones de acción
 */
function FiltrosReportes({
  onAplicarFiltros,
  onLimpiarFiltros,
  onRefrescar,
  filtrosActivos = {},
  tipoReporte = "REPORTES_POR_ASESOR",
  autoActualizacion = false,
  intervaloActualizacion = 120000, // 2 minutos por defecto
}) {
  const [filtros, setFiltros] = useState({
    fecha_desde: "",
    fecha_hasta: "",
    asesor_id: "",
    sede_id: "",
    estado_id: "",
    tipo_proceso_id: "",
    validado: "",
    admitido: "",
  });

  const [opciones, setOpciones] = useState({
    asesores: [],
    sedes: [],
    estados: [],
    tipos_proceso: [],
  });

  const [loading, setLoading] = useState(true);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);

  // Cargar opciones de filtros al montar el componente
  useEffect(() => {
    const cargarOpciones = async () => {
      try {
        const response = await ReportesApi.obtenerFiltrosDisponibles();
        // Asegurar que siempre haya arrays, incluso si el backend retorna undefined
        setOpciones({
          asesores: response.data.asesores || [],
          sedes: response.data.sedes || [],
          estados: response.data.estados || [],
          tipos_proceso: response.data.tipos_proceso || [],
        });
        setLoading(false);
      } catch (error) {
        console.error("Error al cargar opciones de filtros:", error);
        // Mantener arrays vacíos en caso de error
        setOpciones({
          asesores: [],
          sedes: [],
          estados: [],
          tipos_proceso: [],
        });
        setLoading(false);
      }
    };

    cargarOpciones();
  }, []);

  // Sincronizar con filtros activos externos
  useEffect(() => {
    if (Object.keys(filtrosActivos).length > 0) {
      setFiltros((prev) => ({ ...prev, ...filtrosActivos }));
    }
  }, [filtrosActivos]);

  // Auto-actualización
  useEffect(() => {
    if (!autoActualizacion) return;

    const intervalo = setInterval(() => {
      if (onRefrescar) {
        onRefrescar();
        setUltimaActualizacion(new Date());
      }
    }, intervaloActualizacion);

    return () => clearInterval(intervalo);
  }, [autoActualizacion, intervaloActualizacion, onRefrescar]);

  const handleChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleAplicar = () => {
    // Filtrar valores vacíos
    const filtrosLimpios = Object.fromEntries(
      Object.entries(filtros).filter(([, value]) => value !== "" && value !== null)
    );

    if (onAplicarFiltros) {
      onAplicarFiltros(filtrosLimpios);
    }
    setUltimaActualizacion(new Date());
  };

  const handleLimpiar = () => {
    const filtrosVacios = {
      fecha_desde: "",
      fecha_hasta: "",
      asesor_id: "",
      sede_id: "",
      estado_id: "",
      tipo_proceso_id: "",
      validado: "",
      admitido: "",
    };

    setFiltros(filtrosVacios);

    if (onLimpiarFiltros) {
      onLimpiarFiltros();
    }
  };

  const handleRefrescar = () => {
    if (onRefrescar) {
      onRefrescar();
      setUltimaActualizacion(new Date());
    }
  };

  const formatearUltimaActualizacion = () => {
    if (!ultimaActualizacion) return "Nunca";

    const ahora = new Date();
    const diferencia = Math.floor((ahora - ultimaActualizacion) / 1000); // segundos

    if (diferencia < 60) return `Hace ${diferencia}s`;
    if (diferencia < 3600) return `Hace ${Math.floor(diferencia / 60)}m`;
    return `Hace ${Math.floor(diferencia / 3600)}h`;
  };

  // Determinar qué filtros mostrar según el tipo de reporte
  const mostrarFiltroAsesor = tipoReporte === "REPORTES_POR_ASESOR" || tipoReporte === "todos";
  const mostrarFiltroSede =
    tipoReporte === "REPORTES_POR_SEDE" ||
    tipoReporte === "REPORTES_POR_ASESOR" ||
    tipoReporte === "todos";
  const mostrarFiltroEstado = tipoReporte !== "REPORTES_TIEMPOS_GESTION";
  const mostrarFiltroTipo =
    tipoReporte === "REPORTES_POR_TIPO" ||
    tipoReporte === "REPORTES_POR_ASESOR" ||
    tipoReporte === "todos";
  const mostrarFiltrosBooleanos = tipoReporte === "REPORTES_POR_ASESOR" || tipoReporte === "todos";

  return (
    <Card>
      <SuiBox p={1}>
        {/* Header con toggle */}
        <SuiBox display="flex" justifyContent="space-between" alignItems="center">
          <SuiBox display="flex" alignItems="center" gap={1}>
            <FilterListIcon size="large" />
            <SuiTypography variant="h5">Filtros de Búsqueda</SuiTypography>
            {ultimaActualizacion && (
              <SuiTypography variant="caption" textColor="text" sx={{ ml: 2 }}>
                Última actualización: {formatearUltimaActualizacion()}
              </SuiTypography>
            )}
          </SuiBox>

          <SuiBox display="flex" gap={1}>
            <Tooltip title="Refrescar datos">
              <IconButton size="large" onClick={handleRefrescar} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={mostrarFiltros ? "Ocultar filtros" : "Mostrar filtros"}>
              <IconButton size="large" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </SuiBox>
        </SuiBox>

        {/* Filtros colapsables */}
        <Collapse in={mostrarFiltros}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mt: 1 }}>
            <Grid size={3}>
              <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                Fecha Desde
              </SuiTypography>
              <TextField
                fullWidth
                type="date"
                value={filtros.fecha_desde}
                onChange={(e) => handleChange("fecha_desde", e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={3}>
              <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                Fecha Hasta
              </SuiTypography>
              <TextField
                fullWidth
                type="date"
                value={filtros.fecha_hasta}
                onChange={(e) => handleChange("fecha_hasta", e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            {mostrarFiltroAsesor && (
              <Grid size={3}>
                <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                  Asesor
                </SuiTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={filtros.asesor_id}
                    onChange={(e) => handleChange("asesor_id", e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {(opciones.asesores || []).map((asesor) => (
                      <MenuItem key={asesor.id} value={asesor.id}>
                        {asesor.nombre_completo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {mostrarFiltroSede && (
              <Grid size={3}>
                <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                  Sede
                </SuiTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={filtros.sede_id}
                    onChange={(e) => handleChange("sede_id", e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {(opciones.sedes || []).map((sede) => (
                      <MenuItem key={sede.id} value={sede.id}>
                        {sede.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mt: 1 }}>
            {mostrarFiltroEstado && (
              <Grid size={3}>
                <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                  Estado
                </SuiTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={filtros.estado_id}
                    onChange={(e) => handleChange("estado_id", e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {(opciones.estados || []).map((estado) => (
                      <MenuItem key={estado.id} value={estado.id}>
                        {estado.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {mostrarFiltroTipo && (
              <Grid size={3}>
                <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                  Tipo Proceso
                </SuiTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={filtros.tipo_proceso_id}
                    onChange={(e) => handleChange("tipo_proceso_id", e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {(opciones.tipos_proceso || []).map((tipo) => (
                      <MenuItem key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {mostrarFiltrosBooleanos && (
              <Grid size={3}>
                <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                  Validado
                </SuiTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={filtros.validado}
                    onChange={(e) => handleChange("validado", e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Sí</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {mostrarFiltrosBooleanos && (
              <Grid size={3}>
                <SuiTypography variant="body2" fontWeight="light" sx={{ mb: 0.5 }}>
                  Admitido
                </SuiTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={filtros.admitido}
                    onChange={(e) => handleChange("admitido", e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Sí</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {/* Botones de acción */}
          <SuiBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <SuiButton
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleLimpiar}
            >
              Limpiar
            </SuiButton>
            <SuiButton
              variant="gradient"
              color="primary"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={handleAplicar}
            >
              Aplicar Filtros
            </SuiButton>
          </SuiBox>
        </Collapse>
      </SuiBox>
    </Card>
  );
}

FiltrosReportes.propTypes = {
  onAplicarFiltros: PropTypes.func,
  onLimpiarFiltros: PropTypes.func,
  onRefrescar: PropTypes.func,
  filtrosActivos: PropTypes.object,
  tipoReporte: PropTypes.oneOf([
    "REPORTES_POR_ASESOR",
    "REPORTES_POR_SEDE",
    "REPORTES_POR_TIPO",
    "REPORTES_TIEMPOS_GESTION",
    "todos",
  ]),
  autoActualizacion: PropTypes.bool,
  intervaloActualizacion: PropTypes.number,
};

export default FiltrosReportes;
